import { omit } from 'remeda';
import z from 'zod';

import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import {
    CURRENT_VERSION,
    ElementId,
    emptyChildren,
    EmptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnityBluesky = z.object({
    type: z.literal(ELEMENT_TYPES.BLUESKY),
    mimetype: z.literal(MIME_TYPES.BLUESKY),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    originalSrc: z.string(),
    src: z.string(),
    contentAttributes: z.object({
        caption: z.object({
            richTextFormat: z.array(UnityParagraph),
        }),
        credit: z.string().nullable(),
    }),
});
export type UnityBluesky = z.infer<typeof UnityBluesky>;

export const PlateBluesky = z.object({
    type: z.literal(ELEMENT_TYPES.BLUESKY),
    id: ElementId,
    data: z.object({
        mimetype: z.literal(MIME_TYPES.BLUESKY),
        templateElement: z.boolean().optional(),
        originalSrc: z.string(),
        src: z.string(),
        embed: z.object({
            caption: z.array(PlateParagraph),
            credit: z.string().nullable(),
        }),
    }),
    children: EmptyChildren,
});
export type BlueskyElement = z.infer<typeof PlateBluesky>;

export const BlueskyCodec = z.codec(UnityBluesky, PlateBluesky, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            mimetype: value.mimetype,
            originalSrc: value.originalSrc,
            src: value.src,
            embed: {
                caption: value.contentAttributes.caption.richTextFormat.map((paragraph) =>
                    ParagraphCodec.decode(paragraph),
                ),
                credit: value.contentAttributes.credit,
            },
            ...(value.templateElement && { templateElement: value.templateElement }),
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        ...omit(value.data, ['embed']),
        contentAttributes: {
            caption: {
                richTextFormat: value.data.embed.caption.map((paragraph) =>
                    ParagraphCodec.encode(paragraph),
                ),
            },
            credit: value.data.embed.credit,
        },
    }),
});
